import React, { useState } from 'react'
import { Check, Minus } from 'lucide-react'

export interface CheckboxProps {
    variant?: 'primary' | 'secondary'
    checked?: boolean
    defaultChecked?: boolean
    indeterminate?: boolean
    onChange?: (checked: boolean) => void
    title?: string
    description?: string
    error?: string
    disabled?: boolean
    /** Accessible name for standalone checkboxes that render without a visible title (e.g. table row selectors). */
    ariaLabel?: string
    style?: React.CSSProperties
}

export const Checkbox = ({
    variant = 'primary',
    checked: controlledChecked,
    defaultChecked = false,
    indeterminate = false,
    onChange,
    title,
    description,
    error,
    disabled = false,
    ariaLabel,
    style,
}: CheckboxProps) => {
    // Both variants currently render the same control background (matches the source library)
    void variant

    const [internalChecked, setInternalChecked] = useState(defaultChecked)
    const isChecked = controlledChecked !== undefined ? controlledChecked : internalChecked

    const handleToggle = () => {
        if (disabled) return
        const next = !isChecked
        if (controlledChecked === undefined) setInternalChecked(next)
        onChange?.(next)
    }

    const isActive = isChecked || indeterminate
    const hasError = !!error
    const hasLabelArea = !!(title || description || error)

    const controlClasses = [
        // The nudge exists to line the 16px control up with the cap-height of a 20px first label
        // line — so it only makes sense when there IS a label. A bare checkbox (table row selector,
        // a list where the caption is a sibling) has nothing to align to, and the nudge instead
        // pushes it off whatever is centring it: measured 1px below the centre of every row cell.
        hasLabelArea ? 'mt-0.5' : '',
        'flex size-4 shrink-0 items-center justify-center rounded-md border-2 p-0 transition-colors duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
        isActive ? 'bg-accent text-accent-foreground' : 'bg-surface',
        !isActive && !disabled ? 'group-hover:bg-default-hover' : '',
        hasError ? 'border-danger' : isActive ? 'border-accent' : 'border-foreground-muted',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
    ].join(' ')

    return (
        <label className={`group flex items-start gap-2 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`} style={style}>
            {/* Control */}
            <button
                type="button"
                role="checkbox"
                aria-checked={indeterminate ? 'mixed' : isChecked}
                aria-label={ariaLabel}
                disabled={disabled}
                onClick={handleToggle}
                className={controlClasses}
            >
                {indeterminate ? <Minus size={12} strokeWidth={1.5} /> : isChecked ? <Check size={12} strokeWidth={1.5} /> : null}
            </button>

            {/* Label area */}
            {(title || description || error) && (
                <div className="flex min-w-0 flex-col">
                    {title && <span className="text-sm font-medium text-foreground">{title}</span>}
                    {description && <span className="text-xs font-normal text-foreground-muted">{description}</span>}
                    {error && <span className="mt-0.5 text-xs font-normal text-danger">{error}</span>}
                </div>
            )}
        </label>
    )
}
